import React from "react";
import AdvancedTableWrapper from "@civicplus/preamble-ui/lib/AdvancedTable/AdvancedTableWrapper";
import Titlebar from "@civicplus/preamble-ui/lib/Titlebar";
import { bottle } from "../../../provider/Bottle";
import { buildDateFilter, buildUserFilterFromOptionShape } from "../../../util/FilterHelper";
import { useNavigate } from "react-router";
import { MessageService } from "../../../services/MessageService";
import { OrganizationSettingsService } from "../../../services/OrganizationSettingsService";
import { searchUsers } from "../../../util/UserSearch";
import { TableData, TableState } from "@civicplus/preamble-ui/lib/AdvancedTable/AdvancedTableWrapperConstants";
import {
    ByUser,
    LastModified,
    makeDateRangeFilter,
    makeUsersFilter
} from "@civicplus/preamble-ui/lib/Utilities/CommonTableComponents";
import { debounce } from "../../../util/Debounce";

// Retention is up to 84 months and the server groups every message in the requested range into one row per message, so
// an unbounded request gets slower as an organization sends more. Start from the last 90 days; the Date Sent filter can
// go further back, and clearing it shows everything.
const DEFAULT_DATE_RANGE_DAYS = 90;

const getDefaultDateFilter = (): string[] => {
    const from = new Date();
    from.setDate(from.getDate() - DEFAULT_DATE_RANGE_DAYS);
    from.setHours(0, 0, 0, 0);

    // The [from, until] pair the Date Sent filter holds; an empty until means "until now".
    return [from.toISOString(), ""];
};

const GlobalMessageHistory: React.FC = () => {
    const messageService: MessageService = bottle.container.MessageService;
    const settingsService: OrganizationSettingsService = bottle.container.OrganizationSettingsService;
    const navigate = useNavigate();

    const timeZone = settingsService.settings.defaultListSendTimeZoneLabel;
    const defaultDateFilter = React.useMemo(getDefaultDateFilter, []);
    const columns = [
        {
            name: "id",
            options: { display: "excluded", filter: false, sort: false }
        },
        {
            name: "subject",
            label: "Subject",
            options: {
                filter: false,
                sort: false
            }
        },
        {
            name: "dateSent",
            label: "Date Sent",
            options: {
                ...makeDateRangeFilter("Date Sent", timeZone, true),
                filterList: defaultDateFilter,
                sort: true,
                filter: true
            }
        },
        {
            name: "sender",
            label: "Sender",
            options: {
                ...makeUsersFilter("Sender", debounce(searchUsers, 300), null, true),
                filter: true,
                sort: false
            }
        },
        {
            name: "listId",
            options: { display: "excluded", filter: false, sort: false }
        },
        {
            name: "emailCount",
            label: "Email Sent",
            options: {
                filter: false,
                sort: false
            }
        },
        {
            name: "smsCount",
            label: "Sms Sent",
            options: {
                filter: false,
                sort: false
            }
        }
    ];

    const onRowClick = (
        data: string[],
        meta: {
            dataIndex: number;
            rowIndex: number;
        },
        event: unknown
    ) => {
        const orgId = bottle.container.OrgService.orgId!;
        const messageId = data[0];
        navigate(`/${orgId}/admin/messages/history/${messageId}`);
    };

    const getRows = async (tableState: TableState): Promise<TableData> => {
        // The table keeps an empty filterList until the user changes a filter, so the column default above is only shown,
        // not sent. Until then use the same default here; once the user changes a filter their Date Sent choice,
        // including clearing it, is used as is.
        const dateFilter = (tableState.filterList?.length ?? 0) === 0 ? defaultDateFilter : tableState.filterList[2];

        const messages = await messageService.getMessageHistory(
            tableState.page,
            tableState.rowsPerPage,
            null,
            tableState.sortOrder.name || "id",
            tableState.sortOrder.direction || "desc",
            {
                ...buildDateFilter(dateFilter, "dateSent"),
                ...buildUserFilterFromOptionShape(tableState.filterList[3], "senderId")
            }
        );

        const data = messages.items.map((m) => {
            const dataRow: unknown[] = [
                m.id,
                m.subject,
                <LastModified
                    key={`lastmodified-${m.id}`}
                    date={m.dateSent!}
                    organizationTimeZone={settingsService.settings.defaultListSendTimeZoneLabel}
                />,
                <ByUser users={new Map()} entity={m.sender!} />,
                m.listIds == null ? 0 : m.listIds[0],
                m.emailCount,
                m.smsCount
            ];

            return dataRow;
        });

        return { count: messages.count, data };
    };

    return (
        <>
            <Titlebar id="titlebar" title="Message History" />

            <AdvancedTableWrapper
                columns={columns}
                rows={getRows}
                emptyMessage={`No messages were sent in the selected date range. Only the last ${DEFAULT_DATE_RANGE_DAYS} days are shown until you change the Date Sent filter.`}
                scrollToTop={true}
                showFilter={true}
                download={false}
                initialSortDirection="desc"
                initialSortColumn="dateSent"
                initialSortColumnIndex={2}
                rowsPerPage={25}
                rowsPerPageOptions={[25, 50, 100]}
                onRowClick={onRowClick}
            />
        </>
    );
};

export default GlobalMessageHistory;
